import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useOutletContext, useParams } from 'react-router';
import type { MembershipSummary } from '../../../src/shared/auth';
import type {
  BillingResponse,
  CheckoutBody,
  SubscriptionStatus,
  UsageKind,
} from '../../../src/shared/billing';
import { billingKeys, createCheckout, createPortal, fetchBilling } from '../../api/billing';
import { ErrorNotice } from '../../components/ErrorNotice';
import { LoadingScreen } from '../../components/LoadingScreen';

/**
 * `/w/:slug/settings/billing` — the owner-only billing surface
 * (ARCHITECTURE_PLAN §9 `GET /billing | POST /billing/checkout |
 * POST /billing/portal`; §10 /settings route group). Reports:
 *   - current plan + the authoritative Stripe subscription state mirrored in D1
 *     (§7.3 — Stripe is the source of truth, D1 mirrors it);
 *   - current-period usage meters vs the matching `plan_limits` row (§8);
 *   - Upgrade action → `POST /billing/checkout` → redirect to Stripe's hosted
 *     Checkout URL (no card data ever touches the Worker);
 *   - Manage Billing action → `POST /billing/portal` → redirect to Stripe's
 *     hosted Billing Portal (card, invoices, cancellation).
 *
 * The server is the RBAC authority (`requireRole('owner')`, §6.3); this page
 * additionally hides itself from non-owners so the nav surface is honest.
 */

const UPGRADEABLE_PLANS: Exclude<CheckoutBody['plan'], never>[] = ['solo', 'business', 'growth'];

const PLAN_LABELS: Record<BillingResponse['plan'], string> = {
  trial: 'Trial',
  solo: 'Solo',
  business: 'Business',
  growth: 'Growth',
};

const USAGE_LABELS: Record<UsageKind, string> = {
  voice_minutes: 'Voice minutes',
  sms_segments: 'SMS segments',
  ai_units: 'AI units',
};

function formatPeriodEnd(epochSeconds: number | null): string {
  if (epochSeconds === null) return '—';
  return new Date(epochSeconds * 1000).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatPrice(cents: number): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

/** Visible subscription-status badge. `null` mirrors a never-Stripe tenant. */
function statusLabel(status: SubscriptionStatus): string {
  if (status === null) return 'No subscription';
  switch (status) {
    case 'trialing':
      return 'Trialing';
    case 'active':
      return 'Active';
    case 'past_due':
      return 'Past due';
    case 'canceled':
      return 'Canceled';
    case 'incomplete':
      return 'Incomplete';
    case 'incomplete_expired':
      return 'Expired';
    case 'unpaid':
      return 'Unpaid';
    case 'paused':
      return 'Paused';
  }
}

function statusClassName(status: SubscriptionStatus): string {
  if (status === null) return 'bg-slate-100 text-slate-700';
  switch (status) {
    case 'active':
    case 'trialing':
      return 'bg-green-100 text-green-700';
    case 'past_due':
    case 'unpaid':
      return 'bg-amber-100 text-amber-800';
    case 'canceled':
    case 'incomplete_expired':
      return 'bg-red-100 text-red-700';
    case 'incomplete':
    case 'paused':
      return 'bg-slate-100 text-slate-700';
  }
}

/** A single usage meter row: label, used / limit, and a progress bar. */
function UsageMeter({
  kind,
  used,
  limit,
}: {
  kind: UsageKind;
  used: number;
  limit: number;
}): React.JSX.Element {
  const safeLimit = limit > 0 ? limit : 1;
  const ratio = Math.min(used / safeLimit, 1);
  const pct = Math.round(ratio * 100);
  const atOrOver = used >= limit && limit > 0;
  const barColor = atOrOver ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-slate-900';
  return (
    <div data-testid={`usage-meter-${kind}`} className="space-y-1.5">
      <div className="flex items-baseline justify-between text-sm">
        <span className="font-medium text-slate-900">{USAGE_LABELS[kind]}</span>
        <span className="text-slate-600" data-testid={`usage-value-${kind}`}>
          {used.toLocaleString()} / {limit.toLocaleString()}
        </span>
      </div>
      <div
        className="h-2 w-full overflow-hidden rounded-full bg-slate-100"
        role="progressbar"
        aria-valuenow={used}
        aria-valuemin={0}
        aria-valuemax={limit}
        aria-label={`${USAGE_LABELS[kind]} usage`}
      >
        <div
          className={`h-full rounded-full transition-all ${barColor}`}
          style={{ width: `${pct}%` }}
          data-testid={`usage-bar-${kind}`}
        />
      </div>
      {atOrOver && (
        <p className="text-xs text-red-600" data-testid={`usage-over-${kind}`}>
          Plan limit reached — upgrade to keep sending.
        </p>
      )}
    </div>
  );
}

function redirectTo(url: string): void {
  // Stripe returns a fully-qualified hosted URL; the SPA hands the browser to
  // it via `window.location.assign`. We use `assign` rather than `replace` so
  // the back button can return the user to the billing page after checkout.
  window.location.assign(url);
}

/** Choose the smallest plan above the current one as the default upgrade pick. */
function defaultUpgradePlan(current: BillingResponse['plan']): CheckoutBody['plan'] {
  const order: BillingResponse['plan'][] = ['trial', 'solo', 'business', 'growth'];
  const idx = order.indexOf(current);
  const next = order[idx + 1];
  return (next ?? 'growth') as CheckoutBody['plan'];
}

function BillingForm({ slug, data }: { slug: string; data: BillingResponse }): React.JSX.Element {
  const queryClient = useQueryClient();
  const [selectedPlan, setSelectedPlan] = useState<CheckoutBody['plan']>(() =>
    defaultUpgradePlan(data.plan)
  );

  const invalidateBilling = () => {
    void queryClient.invalidateQueries({ queryKey: billingKeys.all(slug) });
  };

  const checkoutMutation = useMutation({
    mutationFn: (body: CheckoutBody) => createCheckout(slug, body),
    onSuccess: (res) => {
      // Hand off to Stripe's hosted Checkout before invalidating; invalidation
      // is harmless if the user returns via the back button.
      redirectTo(res.url);
      invalidateBilling();
    },
  });

  const portalMutation = useMutation({
    mutationFn: () => createPortal(slug),
    onSuccess: (res) => {
      redirectTo(res.url);
      invalidateBilling();
    },
  });

  const usageByKind = useMemo(() => {
    const map = new Map<UsageKind, number>();
    for (const counter of data.usage) {
      map.set(counter.kind, (map.get(counter.kind) ?? 0) + counter.quantity);
    }
    return map;
  }, [data.usage]);

  const voiceUsed = usageByKind.get('voice_minutes') ?? 0;
  const smsUsed = usageByKind.get('sms_segments') ?? 0;

  return (
    <div className="space-y-6">
      {/* Plan + subscription card */}
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-sm font-medium text-slate-700">Current plan</h2>
            <p
              className="mt-1 text-2xl font-semibold tracking-tight text-slate-900"
              data-testid="plan-name"
            >
              {PLAN_LABELS[data.plan]}
            </p>
            <p className="mt-1 text-sm text-slate-500" data-testid="plan-price">
              {formatPrice(data.planLimits.monthlyPriceCents)} / month
            </p>
          </div>
          <div className="text-right">
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusClassName(data.subscriptionStatus)}`}
              data-testid="subscription-status"
            >
              {statusLabel(data.subscriptionStatus)}
            </span>
            <p className="mt-2 text-xs text-slate-500" data-testid="current-period-end">
              Renews: {formatPeriodEnd(data.currentPeriodEnd)}
            </p>
          </div>
        </div>
      </section>

      {/* Usage meters card */}
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-sm font-medium text-slate-700">Current period usage</h2>
        <p className="mt-1 text-xs text-slate-500">
          Counts reset on the next billing cycle. Hard caps are enforced before every outbound send
          or call (§8).
        </p>
        <div className="mt-4 space-y-5">
          <UsageMeter kind="voice_minutes" used={voiceUsed} limit={data.planLimits.voiceMinutes} />
          <UsageMeter kind="sms_segments" used={smsUsed} limit={data.planLimits.smsSegments} />
        </div>
      </section>

      {/* Actions card */}
      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-sm font-medium text-slate-700">Manage billing</h2>
        <p className="mt-1 text-xs text-slate-500">
          Upgrade or manage your subscription through Stripe. Card details are collected on Stripe's
          hosted page and never touch Astralis.
        </p>

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <label
              htmlFor="upgrade-plan-select"
              className="block text-xs font-medium text-slate-600"
            >
              Upgrade to
            </label>
            <select
              id="upgrade-plan-select"
              value={selectedPlan}
              onChange={(e) => setSelectedPlan(e.target.value as CheckoutBody['plan'])}
              className="mt-1 block rounded-md border border-slate-300 px-3 py-1.5 text-sm shadow-sm focus:border-slate-500 focus:outline-none"
              data-testid="upgrade-plan-select"
            >
              {UPGRADEABLE_PLANS.map((plan) => (
                <option key={plan} value={plan}>
                  {PLAN_LABELS[plan]}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={() => checkoutMutation.mutate({ plan: selectedPlan })}
            disabled={checkoutMutation.isPending}
            className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="upgrade-btn"
          >
            {checkoutMutation.isPending ? 'Redirecting…' : 'Upgrade plan'}
          </button>
          <button
            type="button"
            onClick={() => portalMutation.mutate()}
            disabled={portalMutation.isPending}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="portal-btn"
          >
            {portalMutation.isPending ? 'Opening portal…' : 'Manage billing'}
          </button>
        </div>

        {checkoutMutation.isError && (
          <div className="mt-4">
            <ErrorNotice error={checkoutMutation.error} />
          </div>
        )}
        {portalMutation.isError && (
          <div className="mt-4">
            <ErrorNotice error={portalMutation.error} />
          </div>
        )}
      </section>
    </div>
  );
}

export function BillingPage(): React.JSX.Element {
  const { slug } = useParams<'slug'>();
  const { membership } = useOutletContext<{ membership: MembershipSummary }>();

  const billingQuery = useQuery({
    queryKey: billingKeys.detail(slug!),
    queryFn: ({ signal }) => fetchBilling(slug!, signal),
    enabled: slug !== undefined,
  });

  if (slug === undefined) {
    return <p className="text-sm text-slate-500">Workspace slug not found.</p>;
  }

  if (membership.role !== 'owner') {
    // Server is the RBAC authority; this surface is the honest UI guard so a
    // non-owner navigating here directly sees an actionable state, not a 403 body.
    return (
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Billing</h1>
        <p className="mt-2 text-sm text-slate-600" data-testid="billing-forbidden">
          Only the workspace owner can manage billing. Ask an owner to upgrade or manage the
          subscription.
        </p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight">Billing</h1>
      <p className="mt-1 text-sm text-slate-500">
        {membership.name} &middot; Plan, usage, and subscription management
      </p>

      <div className="mt-6">
        {billingQuery.isPending && <LoadingScreen />}

        {billingQuery.isError && <ErrorNotice error={billingQuery.error} />}

        {billingQuery.isSuccess && <BillingForm slug={slug} data={billingQuery.data} />}
      </div>
    </div>
  );
}
